<script setup lang="ts">
import type { CardProps } from '~/utils/card'

const props = defineProps<Omit<CardProps, 'type'> & {
  image: string
  title: string
  editionNumber?: string
  releaseYear?: string
  authors?: Array<{
    name: string
    slug: string
  }>
  layout?: 'reading' | 'list'
}>()

const hasMetaData = computed(() => {
  return !!(props.editionNumber || props.releaseYear || props.authors?.length)
})

const { coverFallbackImage, shouldRenderCoverImage, handleCoverImageError } = useCoverImageFallback(() => props.image)
</script>

<template>
  <Card type="book" v-bind="props">
    <template #header>
      <FavoriteButton type="book" :layout="layout" />
    </template>
    <div class="flex">
      <img
        v-if="shouldRenderCoverImage"
        :src="image"
        alt="Omslag"
        :class="cardVariants.image({ type: 'book', size, presentation })"
        @error="handleCoverImageError"
      >
      <img
        v-else
        :src="coverFallbackImage"
        alt="Omslag"
        :class="cardVariants.image({ type: 'book', size, presentation })"
      >
    </div>

    <div v-if="!presentation && hasMetaData" class="flex flex-col items-center gap-s">
      <h5 :class="cardVariants.title({ type: 'book' })" v-text="title" />
      <div class="flex flex-col gap-3xs items-center">
        <span v-if="editionNumber || releaseYear" class="text-caption text-secondary">
          {{ editionNumber }}. utgave · {{ releaseYear }}
        </span>
        <div v-if="authors?.length" class="flex flex-wrap text-caption text-secondary gap-3xs justify-center">
          Av <template v-for="author in authors" :key="author.slug">
            <NuxtLink :to="{ name: 'author', params: { slug: author.slug } }" class="text-caption underline text-secondary break-keep-all after:content-[','] last:after:content-['']">
              {{ author.name }}
            </NuxtLink>
          </template>
        </div>
      </div>
    </div>
  </Card>
</template>
